import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { api } from "../../../services/apiService";
import { trackPaywallEntry, trackPowerSearch } from "../../../utils/tracking";
import { REPLAY_MASK } from "../../../utils/replay-mask";

const MAX_QUERY = 400;
const MIN_QUERY = 3;
const REASON_DAILY = "DAILY_LIMIT_REACHED";
const REASON_BURST = "RATE_LIMIT_REACHED";

// Provenance marker stored on the quiz (Quiz.source) so power-search-built
// quizzes are distinguishable in history/analytics. A claim, not a key — the
// server validates the ids themselves, not this string.
export const POWER_SEARCH_QUIZ_SOURCE = "POWER_SEARCH";

/*
 * THE RELEVANCE BUCKETS, and they are exported because two different things
 * have to agree on them: the LABEL on a result chip, and the FLOOR the quality
 * selector asks the server for.
 *
 * These were separate numbers for about ten minutes and the bug was immediate
 * and invisible: filter to "Strong matches only" (0.5), and if the label
 * thresholds had drifted the user gets back a list of cards reading "Good
 * match". The UI would be contradicting itself on screen, and no amount of
 * server-side correctness would fix it. One constant each, both derived here.
 */
export const RELEVANCE_STRONG = 0.5;
export const RELEVANCE_GOOD = 0.35;

/*
 * Qualitative relevance label for a result (A8). The backend returns a raw
 * cosine `similarity` float (e.g. 0.87); surfacing that decimal to a med
 * student is jargon noise. Bucket it into a plain-language strength label
 * instead. Exported + pure so the thresholds are unit-testable. Non-numeric
 * input yields "" so the caller can omit the chip entirely (mirrors the old
 * `typeof r.similarity === "number"` guard).
 */
export const relevanceLabel = (similarity) => {
  if (typeof similarity !== "number" || Number.isNaN(similarity)) return "";
  if (similarity >= RELEVANCE_STRONG) return "Strong match";
  if (similarity >= RELEVANCE_GOOD) return "Good match";
  return "Related";
};

/*
 * HOW MANY, and the default is the whole point.
 *
 * The server's ceiling is 50 and the first tutor session got exactly 50 — a
 * numbered 1..50 wall of full-length stems that answered "is this useful?"
 * with "no". 50 is what a caller MAY ask for. Ten is a study block: it is a
 * quiz you would actually sit, and it fits on a screen.
 */
export const RESULT_COUNTS = [5, 10, 20, 50];
export const DEFAULT_COUNT = 10;

/*
 * QUALITY, expressed as the floor to send as `minRelevance`.
 *
 * "" means send nothing — the server keeps its configured floor and its
 * deliberate fail-open behaviour (if semantic ranking finds nothing, you still
 * get keyword matches). The two raised floors engage the server's STRICT mode,
 * where an empty result is returned honestly instead of being padded with
 * keyword noise. That honest empty is the fix for "50 results, none of them
 * about breast cancer" — it says the bank has no strong match, which a padded
 * list actively hides.
 *
 * Default is GOOD rather than "Any", because the complaint was irrelevance,
 * not scarcity. A short accurate list beats a long padded one.
 *
 * COUPLED TO A SERVER KNOB, deliberately noted because nothing enforces it.
 * The server engages strict mode only when the requested floor EXCEEDS its own
 * `qlearn.power-search.min-similarity` (0.30 today), and that property is
 * explicitly documented as tunable per environment from runtime config without
 * a redeploy. Raise it to 0.35 or above and the DEFAULT option here silently
 * stops engaging strict mode — it becomes indistinguishable from "Anything
 * related", with no error and nothing on screen to show it. If that floor is
 * ever tuned up, these two values have to move together.
 */
export const QUALITY_ANY = "";
export const QUALITY_OPTIONS = [
  { value: String(RELEVANCE_GOOD), label: "Good matches" },
  { value: String(RELEVANCE_STRONG), label: "Strong matches only" },
  { value: QUALITY_ANY, label: "Anything related" },
];
export const DEFAULT_QUALITY = String(RELEVANCE_GOOD);

// The exam-scope select's sentinels. Distinct from "" so neither can ever be
// confused with an exam type whose name is empty.
export const EXAM_SCOPE_ALL = "__ALL__";
/** "Whatever the form has checked" — the only option that can carry >1 exam. */
export const EXAM_SCOPE_FORM = "__FORM__";

/**
 * The exam types a given control value actually searches. Pure, so the one
 * decision that governs both the request and the label on the results can be
 * tested without rendering anything.
 */
export const resolveScope = (scopeValue, formScope) => {
  if (scopeValue === EXAM_SCOPE_ALL) return [];
  if (scopeValue === EXAM_SCOPE_FORM) return [...formScope];
  return [scopeValue];
};

// Names the quiz after the query so it is recognizable in quiz history.
// Truncates by code point (Array.from), not code unit, so a 60-char cut can
// never split a surrogate pair and leave a lone surrogate before the ellipsis.
export const powerSearchQuizName = (query) => {
  const q = String(query || "").replace(/\s+/g, " ").trim();
  const points = Array.from(q);
  const title = points.length > 60 ? `${points.slice(0, 60).join("")}…` : q;
  return title ? `Power Search: ${title}` : "Power Search quiz";
};

/*
 * Params for building a quiz from a Power Search result set, funneled through
 * the SAME createQuiz action as the form (backend: powerSearchQuestionIds on
 * POST /api/v2/quiz, server-revalidated, flag-gated). Pure so the contract is
 * unit-testable. Deliberate divergences from the form defaults:
 *  - questionOptions all-false: the user picked these exact questions; the
 *    form's `unused: true` default would silently EXCLUDE any they already
 *    attempted.
 *  - thresholdEnabled false: a High-Yield q-score filter would re-filter the
 *    explicit picks server-side and drop results below the floor.
 *  - noOfQuestions = ids.length: practice the whole result set (the server
 *    clamps down if a result went stale between search and click).
 */
export const buildPowerSearchQuizParams = ({
  userId,
  questionIds,
  query,
  examTypes,
  isTutor,
  tutorMode,
  timerMode,
}) => ({
  name: powerSearchQuizName(query),
  userId,
  source: POWER_SEARCH_QUIZ_SOURCE,
  quizOptions: {
    // Same rule as onCreateQuiz: tutor-mode only applies to tutor-role users.
    tutorOn: isTutor ? tutorMode : false,
    timerOn: timerMode,
    thresholdEnabled: false,
  },
  noOfQuestions: questionIds.length,
  questionOptions: {
    all: false,
    unused: false,
    correct: false,
    incorrect: false,
    marked: false,
  },
  // The exam scope the search RAN with (snapshotted by the panel, not the
  // click-time form selection); omitted (like the search payload) when no
  // exam was selected — the server treats absent as "no filter".
  ...(examTypes && examTypes.length ? { categoryMap: { examType: [...examTypes] } } : {}),
  powerSearchQuestionIds: [...questionIds],
});

// One-tap example queries so a first-time user isn't staring at a blank box
// wondering what to type — each is a plain-language ABSITE scenario that shows
// off "describe it, don't keyword it".
const EXAMPLE_QUERIES = [
  "bleeding duodenal ulcer",
  "workup of a thyroid nodule",
  "postoperative fever",
];

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  header: { display: "flex", alignItems: "center", gap: 8 },
  beta: { fontSize: 11, fontWeight: 700, letterSpacing: 0.4 },
  sub: { color: theme.palette.text.secondary, marginBottom: theme.spacing(1.5) },
  examples: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: theme.spacing(1.5) },
  examplesLabel: { fontSize: 12, color: theme.palette.text.secondary, marginRight: 2 },
  exampleChip: { cursor: "pointer" },
  row: { display: "flex", gap: 8, alignItems: "flex-start" },
  field: { flex: 1 },
  // Wraps on a phone rather than squeezing three selects onto one line.
  controls: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: theme.spacing(1.5),
    alignItems: "center",
  },
  control: { minWidth: 132 },
  searching: { marginTop: theme.spacing(2) },
  searchingLabel: { display: "block", marginTop: 6, color: theme.palette.text.secondary },
  meter: { marginTop: theme.spacing(1.5) },
  meterLabel: { fontSize: 12, color: theme.palette.text.secondary, marginBottom: 4 },
  upsell: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    // Theme-aware container (A5): light keeps the original blue-tint literals as
    // fallbacks; dark supplies a lighter translucent blue (tokens in themes.js)
    // so the upsell keeps a visible container on the dark surface.
    background: "var(--power-upsell-bg, rgba(30,79,214,0.06))",
    border: "1px solid var(--power-upsell-border, rgba(30,79,214,0.2))",
  },
  results: { marginTop: theme.spacing(2), display: "flex", flexDirection: "column", gap: 8 },
  countsLine: { fontSize: 12, color: theme.palette.text.secondary },
  tagRow: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 },
  tagChip: { fontSize: 11, height: 20 },
  hyChip: { fontSize: 11, height: 20, fontWeight: 700 },
  buildRow: { display: "flex", justifyContent: "flex-end", marginTop: 4 },
  result: { padding: theme.spacing(1.5), display: "flex", gap: 12, alignItems: "flex-start" },
  rank: { fontWeight: 700, color: theme.palette.text.secondary, minWidth: 22 },
  stem: { fontSize: 13, lineHeight: 1.5 },
  empty: { marginTop: theme.spacing(2) },
  emptyTitle: { fontWeight: 600 },
  emptyHint: { marginTop: 4, color: theme.palette.text.secondary, lineHeight: 1.5 },
  error: { color: theme.palette.error.main, marginTop: theme.spacing(1) },
}));

/*
 * Up to two short category tags per result — first discipline + first organ
 * system from the hydrated question's categories (design §3 step 2's
 * "category tags"). Capped so a many-category question can't turn a result
 * row into a chip wall.
 */
export function categoryTags(categories) {
  if (!categories) return [];
  // First entry only when it is a plain string — an object-shaped entry would
  // otherwise render an "[object Object]" chip. De-duped: a discipline and an
  // organ system sharing a label must not collide as React keys.
  const first = (v) => (Array.isArray(v) && typeof v[0] === "string" && v[0] ? v[0] : null);
  return [...new Set([first(categories.discipline), first(categories.organSystem)].filter(Boolean))];
}

/*
 * 160, down from 240. Ten results at 240 characters is roughly three full
 * screens of prose to scroll before reaching the "Practice these" button —
 * which is the actual complaint behind "takes up the entire screen without
 * much value". 160 characters is the patient and the presentation, which is
 * all you need to decide whether a question belongs in your quiz.
 */
export const SNIPPET_CHARS = 160;

function snippet(text, n = SNIPPET_CHARS) {
  if (!text) return "(question text unavailable)";
  const s = text.replace(/\s+/g, " ").trim();
  // Truncate by code point so the cut can never split a surrogate pair
  // (same rule as powerSearchQuizName).
  const points = Array.from(s);
  return points.length > n ? `${points.slice(0, n).join("")}…` : s;
}

/**
 * Flag-gated semantic "Search by meaning" entry point. Calls the backend
 * POST /qlearn/quiz/power-search (404 while its flag is off — handled
 * gracefully), hydrates stems for display, and renders the per-user allowance
 * meter + upsell. Self-contained: it holds its own state and does not touch the
 * surrounding create-quiz flow.
 *
 * `onBuildQuiz(questionIds, query, examTypes)` closes the search→practice
 * loop: the page wires it into its normal createQuiz dispatch (see
 * onPowerSearchBuildQuiz), whose existing success/error effects then navigate
 * into the exam or surface the error. All three args are snapshots of the
 * SERVED search (ids, query text, exam scope), so a user editing the form
 * between search and click cannot drift the quiz away from its results.
 * Optional — without it the results stay read-only. `buildingQuiz` mirrors
 * the page's createQuiz loading state to guard double-taps.
 */
export default function PowerSearchPanel({
  examTypes,
  availableExamTypes,
  onBuildQuiz,
  buildingQuiz = false,
}) {
  const classes = useStyles();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [count, setCount] = useState(DEFAULT_COUNT);
  const [quality, setQuality] = useState(DEFAULT_QUALITY);

  /*
   * EXAM SCOPE, and this control is the actual fix for "it should filter by
   * exam type".
   *
   * The filter was never missing — the panel has always forwarded the form's
   * exam-type checkboxes and the server has always applied them. What was
   * missing was any way to SEE or SET the scope from here. The checkboxes live
   * far up the page in a different section; a user who has not touched them
   * searches the entire bank, gets questions from every exam, and has nothing
   * on screen telling them why. Silently-global is indistinguishable from
   * broken, so it may as well be broken.
   *
   * USUALLY one exam — a tutor builds an ABSITE quiz, not an
   * ABSITE-and-Step-2 quiz — which is why the control is a single select. But
   * "usually" is not "always", and reading it as "always" is exactly what
   * produced the regression this control was supposed to prevent: a form with
   * two exams checked silently searched the whole bank. A multi-exam selection
   * is a first-class option; see defaultScope below.
   */
  const formScope = (Array.isArray(examTypes) ? examTypes : []).filter(
    // A blank exam name would render as an empty option while still being sent
    // as a filter — a scope the control cannot legibly display.
    (e) => typeof e === "string" && e.trim()
  );
  // JSON, not join(","). An exam literally named "A,B" and the pair ["A","B"]
  // produce the same comma-joined string, so the effect would not fire and the
  // panel would keep searching the wrong scope. Exam names come from the API,
  // so this is data-controlled rather than something the code can rule out.
  // Explicit deterministic comparator (S2871): these are exam-name strings, so
  // default sort was already correct — but the signature must be stable across
  // engines/locales, which codepoint comparison guarantees and localeCompare
  // would not.
  const formSignature = JSON.stringify([...formScope].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)));

  const defaultScope = () => {
    if (formScope.length === 0) return EXAM_SCOPE_ALL;
    if (formScope.length === 1) return formScope[0];
    // MORE THAN ONE, and collapsing this to "All exams" was a regression.
    // main sent the form's whole array; the first version of this control sent
    // NOTHING for any selection that was not exactly one exam, so a user with
    // ABSITE and USMLE checked — reachable from saved preferences or the form's
    // Select All — silently searched the entire bank, and the built quiz lost
    // its categoryMap too. Keep the form's selection as a first-class choice.
    return EXAM_SCOPE_FORM;
  };

  const [examScope, setExamScope] = useState(defaultScope);

  // Follow the form when its selection changes, so the two can never disagree
  // about what is about to be searched. Keyed on the signature rather than the
  // array so a re-render with an equal-but-new array does not clobber an
  // explicit choice made here.
  useEffect(() => {
    setExamScope(defaultScope());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSignature]);

  // The form's own list of every exam type, so this select offers the same
  // choices the checkboxes do. Union with the current scope: a scope inherited
  // from the form must stay selectable even if the list has not loaded yet,
  // otherwise the select silently falls back to All and searches the world.
  const examOptions = [
    ...new Set([
      ...(Array.isArray(availableExamTypes) ? availableExamTypes : []).filter(
        (e) => typeof e === "string" && e.trim()
      ),
      ...(examScope !== EXAM_SCOPE_ALL && examScope !== EXAM_SCOPE_FORM ? [examScope] : []),
    ]),
  ];

  const trimmed = query.trim();
  const canSearch = trimmed.length >= MIN_QUERY && !loading;

  // `override` lets an example chip search its own text immediately, without
  // waiting for the setQuery state update to flush. The button passes its click
  // event here, so only accept a string override (an event is ignored → uses
  // the current query).
  const runSearch = async (override) => {
    const q = (typeof override === "string" ? override : query).trim();
    if (q.length < MIN_QUERY || loading) return;
    setLoading(true);
    setError("");
    try {
      // The scope this search will actually run with, resolved once so the
      // request and the snapshot stored on `data` cannot diverge.
      const scope = resolveScope(examScope, formScope);
      const payload = { query: q, limit: count };
      if (scope.length) payload.examTypes = scope;
      // OMITTED, not sent as 0 — but not for the reason an earlier version of
      // this comment gave. It claimed 0.0 was a distinct "strict mode" request.
      // It is not: the server does max(configuredFloor, requested) and engages
      // strict mode only when the result EXCEEDS the configured floor, so 0.0
      // against a 0.30 floor is behaviourally identical to sending nothing.
      // The guard stays because a payload should say what it means, and because
      // Number("") is 0 — but it is tidiness, not a mode switch.
      if (quality !== QUALITY_ANY) payload.minRelevance = Number(quality);
      const res = await api.powerSearch(payload);

      const status = res?.errStatus || res?.status;
      if (status === 404) {
        setData(null);
        setError("Power Search isn't available yet.");
        return;
      }
      if (res?.errStatus || res?.errMsg || !res) {
        setData(null);
        setError("Search failed. Please try again.");
        return;
      }

      const results = Array.isArray(res.results) ? res.results : [];
      // Funnel events, counts-only (never the query text). Rules:
      //  - fire on the SERVED response, before preview hydration, so a failed
      //    getQuestionsByIds cannot swallow a real search from the metrics;
      //  - allowance-BLOCKED responses emit NOTHING — a capped user re-hitting
      //    Search is not a recall gap, and blocked searches can never convert,
      //    so counting them would contaminate both GA gate metrics.
      const blocked = !!(res.allowance && res.allowance.allowed === false);
      if (!blocked) {
        const wordCount = q.trim().split(/\s+/).filter(Boolean).length;
        trackPowerSearch("power_search_performed", {
          result_count: results.length,
          semantic_applied: !!res.semanticApplied,
          fallback_used: !!res.fallbackUsed,
          word_count: wordCount,
          exam_type_count: scope.length,
          scope_all: scope.length === 0,
          quality: quality === QUALITY_ANY ? "any" : quality,
        });
        if (results.length === 0) {
          trackPowerSearch("power_search_zero_results", {
            word_count: wordCount,
            exam_type_count: scope.length,
            scope_all: scope.length === 0,
            quality: quality === QUALITY_ANY ? "any" : quality,
          });
        }
      }
      const stems = {};
      const cats = {};
      if (results.length) {
        const ids = results.map((r) => r.questionId);
        const q = await api.getQuestionsByIds(ids);
        const list = Array.isArray(q) ? q : q?.questions || q?.data || [];
        (list || []).forEach((item) => {
          const id = item && (item.questionId || item.id || item._id);
          if (id) {
            stems[id] = item.stem;
            cats[id] = item.categories || null;
          }
        });
      }
      setData({
        // The query and exam scope these results answer (not the live input
        // box / form selection, which the user may have changed since) — the
        // build-quiz CTA must describe the served result set, not the current
        // form state, or the created quiz's metadata drifts from its contents.
        query: q,
        examTypes: scope,
        // The quality bar this result set was served under, so the empty state
        // can offer the one remedy that actually applies (lower the bar)
        // instead of only telling the user to reword their query.
        quality,
        allowance: res.allowance || null,
        semanticApplied: !!res.semanticApplied,
        // True when we have results but couldn't load any previews, so the UI
        // can say so rather than silently showing placeholder cards.
        previewsFailed: results.length > 0 && Object.keys(stems).length === 0,
        // Server-computed high-yield count (null when the score lookup was
        // unavailable — the counts line then omits the High-Yield part).
        highYieldCount: typeof res.highYieldCount === "number" ? res.highYieldCount : null,
        results: results.map((r) => ({ ...r, stem: stems[r.questionId], categories: cats[r.questionId] })),
      });
    } catch (e) {
      setData(null);
      setError("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    // Enter runs the search; Shift+Enter inserts a newline (the field is
    // multiline for long scenarios). Cmd/Ctrl+Enter still works too. Skip while
    // an IME composition is active so composing text isn't submitted early.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      runSearch();
    }
  };

  // Surface how to run the search + why it's disabled, instead of a bare
  // character counter (the too-short state previously left the button silently
  // disabled). Fall back to the counter only as the query nears the limit.
  const helperText =
    trimmed.length > 0 && trimmed.length < MIN_QUERY
      ? `Type at least ${MIN_QUERY} characters to search`
      : query.length > MAX_QUERY * 0.85
      ? `${query.length}/${MAX_QUERY}`
      : "Enter to search · Shift+Enter for a new line";

  const allowance = data?.allowance;
  const blocked = allowance && allowance.allowed === false;

  // §6 gap-fill: the daily-limit Pro upsell (rendered below) is display-only
  // text with no click target, so it previously fired zero analytics. Emit an
  // impression event when it surfaces so the paywall-entry funnel is complete.
  // No markup/style change — tracking-only.
  const dailyProUpsellShown = !!(blocked && allowance.reason === REASON_DAILY && !allowance.pro);
  useEffect(() => {
    if (dailyProUpsellShown) {
      trackPaywallEntry('paywall_entry_shown', { source: 'power_search_upsell' });
    }
  }, [dailyProUpsellShown]);

  return (
    <Paper className={classes.root} elevation={0} variant="outlined" data-testid="power-search-panel">
      <div className={classes.header}>
        <Typography variant="h6">Search by meaning</Typography>
        <Chip size="small" color="primary" label="BETA" className={classes.beta} />
      </div>
      <Typography className={classes.sub} variant="body2">
        Describe what you want in plain language — e.g. “management of a bleeding duodenal ulcer”.
        Power Search ranks matching questions by meaning, not just keywords.
      </Typography>

      <div
        className={classes.examples}
        role="group"
        aria-label="Example searches"
        data-testid="power-search-examples"
      >
        <span className={classes.examplesLabel}>Try:</span>
        {EXAMPLE_QUERIES.map((ex) => (
          <Chip
            key={ex}
            size="small"
            variant="outlined"
            clickable
            // Don't let a chip swap the box text mid-search, or spend an
            // allowance once the daily limit is already hit.
            disabled={loading || blocked}
            label={ex}
            className={classes.exampleChip}
            onClick={() => {
              setQuery(ex);
              runSearch(ex);
            }}
            data-testid="power-search-example"
          />
        ))}
      </div>

      <div className={classes.row}>
        <TextField
          id="power-search-query"
          className={classes.field}
          variant="outlined"
          size="small"
          multiline
          placeholder="Describe the topic or scenario…"
          value={query}
          onChange={(e) => setQuery(e.target.value.slice(0, MAX_QUERY))}
          onKeyDown={onKeyDown}
          inputProps={{ maxLength: MAX_QUERY, "data-testid": "power-search-input", enterKeyHint: 'search' }}
          helperText={helperText}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={runSearch}
          disabled={!canSearch}
          data-testid="power-search-submit"
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Search"}
        </Button>
      </div>

      {/* Native selects on purpose: they are the accessible default, they get
          the real picker on a phone, and they keep this row from becoming
          three MUI popovers on a page that already has plenty. */}
      <div className={classes.controls} data-testid="power-search-controls">
        <TextField
          select
          size="small"
          variant="outlined"
          id="power-search-exam"
          label="Exam"
          className={classes.control}
          value={examScope}
          onChange={(e) => setExamScope(e.target.value)}
          disabled={loading}
          SelectProps={{ native: true, inputProps: { "data-testid": "power-search-exam" } }}
        >
          <option value={EXAM_SCOPE_ALL}>All exams</option>
          {formScope.length > 1 && (
            <option value={EXAM_SCOPE_FORM}>
              Your {formScope.length} selected exams
            </option>
          )}
          {examOptions.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          variant="outlined"
          id="power-search-count"
          label="Show"
          className={classes.control}
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          disabled={loading}
          SelectProps={{ native: true, inputProps: { "data-testid": "power-search-count" } }}
        >
          {RESULT_COUNTS.map((n) => (
            <option key={n} value={n}>
              {n} questions
            </option>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          variant="outlined"
          id="power-search-quality"
          label="Match quality"
          className={classes.control}
          value={quality}
          onChange={(e) => setQuality(e.target.value)}
          disabled={loading}
          SelectProps={{ native: true, inputProps: { "data-testid": "power-search-quality" } }}
        >
          {QUALITY_OPTIONS.map((o) => (
            <option key={o.label} value={o.value}>
              {o.label}
            </option>
          ))}
        </TextField>
      </div>

      {loading && (
        <div className={classes.searching} data-testid="power-search-loading" aria-live="polite">
          <LinearProgress aria-label="Searching by meaning" />
          <Typography variant="caption" className={classes.searchingLabel}>
            Searching by meaning…
          </Typography>
        </div>
      )}

      {error && (
        <Typography className={classes.error} variant="body2" data-testid="power-search-error">
          {error}
        </Typography>
      )}

      {allowance && !allowance.pro && (
        <div className={classes.meter} data-testid="power-search-meter">
          <div className={classes.meterLabel}>
            {Math.max(allowance.dailyRemaining, 0)} of {allowance.dailyLimit} free searches left today
          </div>
          <LinearProgress
            variant="determinate"
            value={
              allowance.dailyLimit > 0
                ? Math.min(100, (allowance.dailyUsed / allowance.dailyLimit) * 100)
                : 0
            }
          />
        </div>
      )}

      {blocked && (
        <div className={classes.upsell} data-testid="power-search-blocked">
          {allowance.reason === REASON_BURST ? (
            <Typography variant="body2">
              You’re searching too fast — please try again in a minute.
            </Typography>
          ) : allowance.reason === REASON_DAILY && !allowance.pro ? (
            <Typography variant="body2" data-testid="power-search-upsell">
              <strong>You’ve used your free Power Searches for today.</strong> Upgrade to Pro for
              unlimited semantic search. Keyword search stays free and unlimited.
            </Typography>
          ) : (
            <Typography variant="body2">
              You’ve reached today’s Power Search limit. Please try again tomorrow.
            </Typography>
          )}
        </div>
      )}

      {data && !blocked && data.previewsFailed && (
        <Typography className={classes.error} variant="body2" data-testid="power-search-previews-failed">
          Found matches, but couldn’t load previews. Please try again.
        </Typography>
      )}

      {data && !blocked && data.results.length > 0 && !data.semanticApplied && (
        <Typography className={classes.sub} variant="caption" data-testid="power-search-fallback">
          Showing keyword matches (semantic ranking unavailable).
        </Typography>
      )}

      {data && !blocked && data.results.length > 0 && (
        <div className={classes.results} data-testid="power-search-results">
          <Typography className={classes.countsLine} data-testid="power-search-counts">
            {data.results.length} match{data.results.length === 1 ? "" : "es"}
            {typeof data.highYieldCount === "number" ? ` · ${data.highYieldCount} High-Yield` : ""}
            {/* The scope this set was SERVED under, not the current control
                value — the two differ the moment a user changes a select
                without re-searching, and quietly relabelling an old result set
                with a new scope is how "it doesn't filter by exam" starts. */}
            {` · ${data.examTypes.length ? data.examTypes.join(", ") : "all exams"}`}
          </Typography>
          {/* data-hj-suppress: this renders QUESTION-BANK STEMS, and
              /create-quiz is on the session-replay ALLOW-list — so unlike every
              other stem-rendering surface in the app, no route denial protects
              it. The sibling more-like-this-panel carries this attribute and is
              pinned in the CONTENT_SURFACES guard, but it only ever renders
              under /exam, which IS denied. This was the one stem surface on an
              allowed route and nobody had pinned it.

              It is dark today (REACT_APP_ENABLE_SEMANTIC_SEARCH is in no tracked
              file) — which is exactly why it has to land now: flipping Power
              Search on is a runtime-config change that gets no code review, and
              it would silently start streaming licensed bank content into
              session replays. See src/utils/session-replay.js. */}
          {data.results.map((r) => (
            <Paper key={r.questionId} className={classes.result} elevation={0} variant="outlined" {...REPLAY_MASK}>
              {/* A rank number is a CLAIM that the list is ordered by how well
                  each result fits. In fallback mode it is not: those come back
                  in the store's own order with no similarity attached, so
                  numbering them asserts a ranking that does not exist. Bullet
                  them instead — the "showing keyword matches" note above then
                  reads as honest rather than as a footnote under a fake
                  ranking. */}
              <span className={classes.rank} data-testid="power-search-rank">
                {data.semanticApplied ? r.rank || "•" : "•"}
              </span>
              <Box flex={1}>
                <Typography className={classes.stem}>{snippet(r.stem)}</Typography>
                {(categoryTags(r.categories).length > 0 || r.highYield === true) && (
                  <div className={classes.tagRow} data-testid="power-search-tags">
                    {r.highYield === true && (
                      <Chip
                        size="small"
                        color="secondary"
                        label="High-Yield"
                        className={classes.hyChip}
                        data-testid="power-search-hy-badge"
                      />
                    )}
                    {categoryTags(r.categories).map((t) => (
                      <Chip key={t} size="small" variant="outlined" label={t} className={classes.tagChip} />
                    ))}
                  </div>
                )}
              </Box>
              {relevanceLabel(r.similarity) && (
                <Chip
                  size="small"
                  label={relevanceLabel(r.similarity)}
                  data-testid="power-search-relevance"
                />
              )}
            </Paper>
          ))}
          {onBuildQuiz && (
            <div className={classes.buildRow}>
              <Button
                variant="contained"
                color="primary"
                disabled={buildingQuiz}
                onClick={() => {
                  // Conversion FIRST, tracking second: even with the helper's
                  // try/catch, nothing may sit between the click and the quiz.
                  onBuildQuiz(data.results.map((r) => r.questionId), data.query, data.examTypes);
                  // The conversion the GA gate watches: search led to a quiz.
                  trackPowerSearch("power_search_build_quiz", {
                    result_count: data.results.length,
                  });
                }}
                data-testid="power-search-build-quiz"
              >
                {buildingQuiz ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  `Practice these ${data.results.length} question${data.results.length === 1 ? "" : "s"}`
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {data && !blocked && !loading && data.results.length === 0 && (
        <div className={classes.empty} data-testid="power-search-empty" role="status">
          <Typography variant="body2" className={classes.emptyTitle}>
            No matches for that
            {data.examTypes.length ? ` in ${data.examTypes.join(", ")}` : ""}.
          </Typography>
          {/* An empty result under a RAISED bar has a one-click remedy, and
              offering only "reword your query" would be misleading — the query
              may be fine and the bank simply has nothing that strong. Name the
              actual cause first. */}
          {data.quality !== QUALITY_ANY ? (
            <Typography variant="body2" className={classes.emptyHint}>
              Nothing cleared the <strong>match quality</strong> bar you set. Widen
              it to “Anything related”, search all exams, or describe the concept
              more broadly — Power Search matches on meaning, not exact wording.
            </Typography>
          ) : (
            <Typography variant="body2" className={classes.emptyHint}>
              Try fewer or broader terms, or drop the specifics (ages, doses, labs) —
              Power Search matches on the clinical concept, not exact wording. The
              examples above are a good starting point.
            </Typography>
          )}
        </div>
      )}
    </Paper>
  );
}
